/**
 * Prompt Runner page
 * Paste any prompt template that uses {{placeholder}} syntax, auto-generate an
 * input form for the detected variables, and assemble the final prompt live.
 *
 * Supports both personas from CLAUDE.md:
 *  - Beginner (Penny): guided, labelled fields, inline help, no jargon.
 *  - Power user (Pro): paste-and-go, keyboard friendly, copy / optimize actions.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Link,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useLocation, useNavigate } from 'react-router-dom';

/** Matches {{placeholder}} tokens (word chars, matching the existing template convention). */
const PLACEHOLDER_REGEX = /\{\{\s*(\w+)\s*\}\}/g;

type FieldKind = 'text' | 'multiline' | 'number';

interface RunnerField {
  name: string;
  label: string;
  kind: FieldKind;
}

/** Extract unique placeholder names, preserving first-seen order. */
function extractPlaceholders(content: string): string[] {
  const found: string[] = [];
  let match: RegExpExecArray | null;
  PLACEHOLDER_REGEX.lastIndex = 0;
  while ((match = PLACEHOLDER_REGEX.exec(content)) !== null) {
    if (!found.includes(match[1])) {
      found.push(match[1]);
    }
  }
  return found;
}

/** Turn "availableData" / "available_data" into "Available Data". */
function humanize(name: string): string {
  const spaced = name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const NUMBER_HINTS = ['max', 'min', 'count', 'number', 'num', 'quantity', 'ideas', 'limit'];
const MULTILINE_HINTS = [
  'objective',
  'context',
  'data',
  'outcome',
  'constraint',
  'description',
  'detail',
  'note',
  'background',
  'summary',
  'goal',
  'instruction',
  'content',
  'text',
  'requirement',
];

/** Best-effort heuristic to pick a sensible input control for a placeholder. */
function inferKind(name: string): FieldKind {
  const lower = name.toLowerCase();
  if (NUMBER_HINTS.some((h) => lower.includes(h))) return 'number';
  if (MULTILINE_HINTS.some((h) => lower.includes(h))) return 'multiline';
  return 'text';
}

/** Replace every {{name}} with its value (empty value leaves a visible gap marker). */
function assemblePrompt(template: string, values: Record<string, string>): string {
  return template.replace(PLACEHOLDER_REGEX, (_full, name: string) => {
    const value = values[name];
    return value && value.trim().length > 0 ? value : `[${humanize(name)}]`;
  });
}

const EXAMPLE_TEMPLATE = `Act as an AI Opportunity Strategist. Your task is to identify and rank the most valuable ways AI could help a user achieve a specific objective.

Input Parameters:

Objective: {{objective}}
Persona: {{persona}}
Context: {{context}}
Available Data: {{availableData}}
Available Tools: {{availableTools}}
Constraints: {{constraints}}
Desired Outcome: {{desiredOutcome}}
Maximum Ideas: {{maximumIdeas}}

Generate up to {{maximumIdeas}} distinct, non-overlapping AI opportunity angles that directly address the objective, persona, tools, data, and constraints.`;

export default function PromptRunnerPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [template, setTemplate] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  // Allow deep-linking a template in via ?template= or ?prompt=.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incoming = params.get('template') || params.get('prompt');
    if (incoming) {
      setTemplate(incoming);
    }
  }, [location.search]);

  const fields = useMemo<RunnerField[]>(
    () =>
      extractPlaceholders(template).map((name) => ({
        name,
        label: humanize(name),
        kind: inferKind(name),
      })),
    [template]
  );

  const finalPrompt = useMemo(() => assemblePrompt(template, values), [template, values]);

  const filledCount = fields.filter((f) => (values[f.name] ?? '').trim().length > 0).length;
  const allFilled = fields.length > 0 && filledCount === fields.length;

  const handleValueChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(finalPrompt);
    setSnackbar({ open: true, message: 'Final prompt copied to clipboard' });
  };

  const handleReset = () => {
    setValues({});
  };

  const handleLoadExample = () => {
    setTemplate(EXAMPLE_TEMPLATE);
    setValues({});
  };

  const handleOptimize = () => {
    navigate(`/optimizer?prompt=${encodeURIComponent(finalPrompt)}`);
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </Link>
        <Typography color="text.primary">Prompt Runner</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Prompt Runner
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paste a prompt template that uses <code>{'{{placeholder}}'}</code> variables. A form is
            built automatically so you can fill in the details and assemble the final prompt.
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Left: template + generated form */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">1. Prompt template</Typography>
                <Button size="small" onClick={handleLoadExample}>
                  Load example
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                placeholder="Paste your prompt template here. Use {{variableName}} where you want to collect input."
              />
              {fields.length > 0 && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    Detected inputs:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {fields.map((f) => (
                      <Chip key={f.name} label={f.label} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">2. Fill in the details</Typography>
                {fields.length > 0 && (
                  <Chip
                    size="small"
                    label={`${filledCount}/${fields.length} filled`}
                    color={allFilled ? 'success' : 'default'}
                  />
                )}
              </Box>

              {fields.length === 0 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  No <code>{'{{placeholders}}'}</code> detected yet. Add variables to your template
                  above (for example <code>{'{{objective}}'}</code>) and a field will appear here for
                  each one.
                </Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  {fields.map((field) => (
                    <TextField
                      key={field.name}
                      fullWidth
                      label={field.label}
                      value={values[field.name] ?? ''}
                      onChange={(e) => handleValueChange(field.name, e.target.value)}
                      type={field.kind === 'number' ? 'number' : 'text'}
                      multiline={field.kind === 'multiline'}
                      minRows={field.kind === 'multiline' ? 3 : undefined}
                      helperText={`Fills {{${field.name}}}`}
                    />
                  ))}
                </Box>
              )}

              {fields.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    startIcon={<RestartAltIcon />}
                    onClick={handleReset}
                    size="small"
                    color="inherit"
                  >
                    Clear inputs
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Right: assembled prompt */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">3. Final prompt</Typography>
                <Box>
                  <Tooltip title="Copy final prompt">
                    <span>
                      <IconButton onClick={handleCopy} disabled={!finalPrompt.trim()} size="small">
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {finalPrompt.trim() ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    maxHeight: 480,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: 13,
                    lineHeight: 1.6,
                  }}
                >
                  {finalPrompt}
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Your assembled prompt will appear here as you type.
                </Typography>
              )}

              {fields.length > 0 && !allFilled && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Unfilled variables are shown as <code>[Label]</code> placeholders in the output.
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                  disabled={!finalPrompt.trim()}
                >
                  Copy prompt
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  onClick={handleOptimize}
                  disabled={!finalPrompt.trim()}
                >
                  Optimize
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
